<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount } from 'vue';
import StepsLayout from '../../shared-components/StepsLayout.vue';
import { ClassicEditor, Bold, Essentials, Italic, Undo, Heading, HeadingButtonsUI, List, Paragraph, Underline, Alignment, type EditorConfig } from 'ckeditor5';
import AddFile from './AddFile.vue';
import AddSingleFile from './AddSingleFile.vue';
import useValidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import PlaneIcon from './icons/PlaneIcon.vue';
import type { Settings } from '../types/general';
import { multipleSelect } from 'multiple-select-vanilla';
import { submitFormData } from '../api/index';
import VueMultiselect from 'vue-multiselect';
import { RecaptchaV2 } from 'vue3-recaptcha-v2';

const props = defineProps<{
  settings: Settings;
}>();

const currentStep = ref(0);
const stepsCount = 3;
const removeFilesTrigger = ref(false);

const yearsGenerator = () => {
  const currentYear = new Date().getFullYear();
  const years: { value: number; key: number }[] = [];
  for (let i = currentYear; i >= 1960; i--) {
    years.push({ value: i, key: i });
  }
  return years;
};

const programsOptions = computed(() => {
  return Object.entries(props.settings.programs).map(([key, value]) => {
    return { key, value };
  });
});
const citizenshipOptions = computed(() => {
  return Object.entries(props.settings.citizenship).map(([key, value]) => {
    return { key, value };
  });
});
const countriesOptions = computed(() => {
  return Object.entries(props.settings.countries).map(([key, value]) => {
    return { key, value };
  });
});

const iamOptions = computed(() => {
  return Object.entries(props.settings.iam).map(([key, value]) => {
    return { key, value };
  });
});

const isTrue = helpers.withMessage('The value must be true', (value) => value === true);
const formData = computed(() => {
  return {
    ...step1Data.value,
    ...step2Data.value,
    ...step3Data.value,
  };
});

const step1Data = ref({
  fullName: '',
  email: '',
  iam: '',
  program: '',
  host: '',
  citizenship: '',
  year: '',
  picture: [],
});

const step2Data = ref({
  storyTitle: '',
  storyDetails: '',
});

const step3Data = ref({
  media: [],
  videoLink1: '',
  videoLink2: '',
  check: false,
  recaptchaToken: '',
});

onBeforeMount(() => {
  const step1DataStor = localStorage.getItem('step1Data');
  const step2DataStror = localStorage.getItem('step2Data');

  if (step1DataStor) {
    Object.assign(step1Data.value, JSON.parse(step1DataStor));
  }
  if (step2DataStror) {
    Object.assign(step2Data.value, JSON.parse(step2DataStror));
  }
});

onMounted(() => {
  multipleSelect('select.custom-select', {
    selectAll: false,
    width: '100%',
    filter: true,
    displayTitle: true,
    renderOptionLabelAsHtml: true,
  });
});

const rules = computed(() => {
  return {
    step1: {
      fullName: {
        required,
      },
      email: {
        required,
        email,
      },
      iam: {
        required,
      },
      program: {
        required,
      },
      host: {
        required,
      },
      citizenship: {
        required,
      },
      picture: {
        required,
      },
    },
    step2: {
      storyTitle: {
        required,
      },
      storyDetails: {
        required,
      },
    },
    step3: {
      check: {
        isTrue,
      },
      recaptchaToken: {
        required,
      },
    },
  };
});

const $step1 = useValidate(rules.value.step1, step1Data.value);
const $step2 = useValidate(rules.value.step2, step2Data.value);
const $step3 = useValidate(rules.value.step3, step3Data.value);

const validateStep = () => {
  if (currentStep.value === 0) {
    $step1.value.$touch();
    if ($step1.value.$error) {
      return false;
    }
  }
  if (currentStep.value === 1) {
    $step2.value.$touch();
    if ($step2.value.$error) {
      return false;
    }
  }
  if (currentStep.value === 2) {
    $step3.value.$touch();
    if ($step3.value.$error) {
      return false;
    }
  }
  return true;
};

const nextStep = () => {
  if (validateStep()) currentStep.value += 1;
  storeToLocalStorage();
};
const startOver = () => {
  currentStep.value = 0;
  const Step1DataDefault = {
    fullName: '',
    email: '',
    iam: '',
    program: '',
    host: '',
    citizenship: '',
    year: '',
    picture: [],
  };
  const Step2DataDefault = {
    storyTitle: '',
    storyDetails: '',
  };
  const Step3DataDefault = {
    media: [],
    videoLink1: '',
    videoLink2: '',
    check: false,
    recaptchaToken: '',
  };

  Object.assign(step1Data.value, Step1DataDefault);
  Object.assign(step2Data.value, Step2DataDefault);
  Object.assign(step3Data.value, Step3DataDefault);

  $step1.value.$reset();
  $step2.value.$reset();
  $step3.value.$reset();

  localStorage.removeItem('step1Data');
  localStorage.removeItem('step2Data');

  removeFilesTrigger.value = true;
};

const ckeditorConfig = {
  editor: ClassicEditor,
  editorData: '<p>Hello from CKEditor 5 in Vue!</p>',
  editorConfig: {
    plugins: [Bold, Essentials, Italic, Paragraph, Undo, Heading, List, HeadingButtonsUI, Underline, Alignment],
    toolbar: ['heading', 'bold', 'italic', 'underline', 'alignment', 'numberedList', 'bulletedList', 'undo', 'redo'],
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      ],
    },
  } as EditorConfig,
};

const addPhoto = (files: any) => {
  step1Data.value.picture = files;
};
const addMedia = (files: any) => {
  step3Data.value.media = files;
};

const submitForm = async () => {
  if (validateStep()) {
    await submitFormData(formData.value);
    currentStep.value += 1;
  }
};

const storeToLocalStorage = () => {
  const newStep1Data = JSON.parse(JSON.stringify(step1Data.value));
  newStep1Data.picture = [];
  localStorage.setItem('step1Data', JSON.stringify(newStep1Data));
  localStorage.setItem('step2Data', JSON.stringify(step2Data.value));
};

function onVerify(token: any) {
  step3Data.value.recaptchaToken = token as string;
}
</script>

<template>
  <div class="share-story-app-wrapper form-wrapper">
    <StepsLayout
      title="Share Your Story in progress"
      :current-step="currentStep"
      :steps-count="stepsCount"
      :is-next="true"
      @next-step="nextStep"
      @start-over="startOver"
      @go-back="currentStep -= 1"
      @submit="submitForm"
    >
      <!-- ========== Step 1 ========== -->
      <div v-show="currentStep === 0">
        <h2 class="mb-40">Introduce Yourself</h2>
        <div
          class="mb-24"
          :class="{ error: $step1.fullName.$error }"
        >
          <label for="full-name">Full Name (required)</label>
          <input
            id="full-name"
            type="text"
            name="full_name"
            v-model="step1Data.fullName"
            placeholder="John Doe"
          />
          <span
            class="error-messasge"
            v-if="$step1.fullName.$error"
          >
            <span class="material-icons-outlined">error_outline</span>
            {{ $step1.fullName.$errors[0].$message }}
          </span>
        </div>
        <div
          class="mb-24"
          :class="{ error: $step1.email.$error }"
        >
          <label for="email">Email Address (required)</label>
          <input
            id="email"
            type="email"
            name="email"
            v-model="step1Data.email"
            placeholder="name@domain.com"
          />
          <span
            class="error-messasge"
            v-if="$step1.email.$error"
            ><span class="material-icons-outlined">error_outline</span>{{ $step1.email.$errors[0].$message }}</span
          >
        </div>
        <div
          class="mb-24"
          :class="{ error: $step1.iam.$error }"
        >
          <label for="iam">I am ... (required) </label>
          <VueMultiselect
            v-model="step1Data.iam"
            :options="iamOptions"
            label="value"
            selectLabel=""
            openDirection="bottom"
            :optionHeight="40"
            placeholder="Select an option"
          >
            <template #noResult>
              <span>No results found</span>
            </template>
          </VueMultiselect>
          <span
            class="error-messasge"
            v-if="$step1.iam.$error"
            ><span class="material-icons-outlined">error_outline</span>{{ $step1.iam.$errors[0].$message }}</span
          >
        </div>
        <div
          class="mb-24"
          :class="{ error: $step1.citizenship.$error }"
        >
          <label for="host">Citizenship (required)</label>
          <VueMultiselect
            v-model="step1Data.citizenship"
            :options="citizenshipOptions"
            label="value"
            selectLabel=""
            openDirection="bottom"
            :optionHeight="40"
            placeholder="Start typing to give a hint"
          >
            <template #noResult>
              <span>No results found</span>
            </template>
          </VueMultiselect>
          <span
            class="error-messasge"
            v-if="$step1.citizenship.$error"
          ><span class="material-icons-outlined">error_outline</span>{{ $step1.citizenship.$errors[0].$message }}</span
          >
        </div>
        <div
          class="mb-24"
          :class="{ error: $step1.host.$error }"
        >
          <label for="host">Host Country (required)</label>
          <VueMultiselect
            v-model="step1Data.host"
            :options="countriesOptions"
            label="value"
            selectLabel=""
            openDirection="bottom"
            :optionHeight="40"
            placeholder="Start typing to give a hint"
          >
            <template #noResult>
              <span>No results found</span>
            </template>
          </VueMultiselect>
          <span
            class="error-messasge"
            v-if="$step1.host.$error"
            ><span class="material-icons-outlined">error_outline</span>{{ $step1.host.$errors[0].$message }}</span
          >
        </div>
        <div class="row">
          <div
            class="col-12 col-md-7 mb-24"
            :class="{ error: $step1.program.$error }"
          >
            <label for="program">Program (required)</label>
            <VueMultiselect
              v-model="step1Data.program"
              :options="programsOptions"
              name="program"
              label="value"
              selectLabel=""
              openDirection="bottom"
              :optionHeight="40"
              placeholder="Select Program"
              class="multiselect--big-options"
            >
              <template #noResult>
                <span>No results found</span>
              </template>
            </VueMultiselect>
            <span
              class="error-messasge"
              v-if="$step1.program.$error"
              ><span class="material-icons-outlined">error_outline</span>{{ $step1.program.$errors[0].$message }}</span
            >
          </div>
          <div class="col-12 col-md-5 mb-24">
            <label for="year">Program Year</label>
            <VueMultiselect
              v-model="step1Data.year"
              :options="yearsGenerator()"
              selectLabel=""
              label="value"
              openDirection="bottom"
              :optionHeight="40"
              placeholder="Select Year"
            >
              <template #noResult>
                <span>No results found</span>
              </template>
            </VueMultiselect>
          </div>
        </div>

        <div>
          <label>Profile Picture (required)</label>
          <AddSingleFile
            v-model="removeFilesTrigger"
            @file-add="addPhoto"
          />
          <span
            class="error-messasge"
            v-if="$step1.picture.$error"
          >
            <span class="material-icons-outlined">error_outline</span>{{ $step1.picture.$errors[0].$message }}
          </span>
        </div>
      </div>

      <!-- ========== Step 2 ========== -->
      <div v-show="currentStep === 1">
        <h2>Tell Your Story</h2>
        <p class="text-m">
          Share a story that illustrates how you connected, partnered, and learned during your Fulbright. Rather than summarizing your entire experience, focus
          on a brief, personal anecdote that truly captures the "Fulbright Spirit" of mutual understanding through travel and exchange.
        </p>

        <div
          class="mb-24 mt-40"
          :class="{ error: $step2.storyTitle.$error }"
        >
          <label for="story-title">Story Title (required)</label>
          <input
            id="story-title"
            type="text"
            name="story_title"
            v-model="step2Data.storyTitle"
            placeholder="e.g. True Story of My Success"
          />
          <span
            class="error-messasge"
            v-if="$step2.storyTitle.$error"
            ><span class="material-icons-outlined">error_outline</span>{{ $step2.storyTitle.$errors[0].$message }}</span
          >
        </div>
        <div
          class="mb-24"
          :class="{ error: $step2.storyDetails.$error }"
        >
          <label for="title-details">Story Details (required) </label>
          <ckeditor
            :editor="ckeditorConfig.editor"
            v-model="step2Data.storyDetails"
            :config="ckeditorConfig.editorConfig"
          />
          <span
            class="error-messasge"
            v-if="$step2.storyDetails.$error"
            ><span class="material-icons-outlined">error_outline</span>{{ $step2.storyDetails.$errors[0].$message }}</span
          >
        </div>
      </div>

      <!-- ========== Step 3 ========== -->
      <div v-show="currentStep === 2">
        <h2>Add Your Media</h2>
        <p class="text-m">
          Adding visuals to your story brings it to life, making your experiences more vivid and impactful. Share your photos and videos to inspire and connect
          with others, illustrating the true essence of your Fulbright journey.
        </p>

        <h3 class="mt-40 mb-20">Add Your Photos</h3>
        <div class="mb-40">
          <AddFile
            v-model="removeFilesTrigger"
            @file-add="addMedia"
          />
        </div>
        <h3 class="mb-24">Add Your Videos</h3>
        <div class="mb-24">
          <label for="story-title">Link to the video 1</label>
          <input
            id="video-link-1"
            type="text"
            name="video_link_1"
            v-model="step3Data.videoLink1"
            placeholder="Insert link"
          />
          <p class="text-subtle-black mt-8">Please submit the video link (e.g. YouTube, Vimeo, Google Drive)</p>
        </div>
        <div class="mb-24">
          <label for="story-title">Link to the video 2</label>
          <input
            id="video-link-2"
            type="text"
            name="video_link_2"
            v-model="step3Data.videoLink2"
            placeholder="Insert link"
          />
          <p class="text-subtle-black mt-8">Please submit the video link (e.g. YouTube, Vimeo, Google Drive)</p>
        </div>
        <div
          class="mt-60"
          :class="{ error: $step2.storyDetails.$error }"
        >
          <input
            id="agree"
            type="checkbox"
            name="agree"
            v-model="step3Data.check"
          />
          <label
            for="agree"
            class="text-m text-royal-blue"
          >
            By clicking "Submit the Story," I agree to the use of my story and materials as outlined in the
            <a
              href="#"
              class="text-red"
            >
              Copyright Release</a
            >.
          </label>
          <span
            class="error-messasge"
            v-if="$step3.check.$error"
            ><span class="material-icons-outlined">error_outline</span>Please agree to the terms</span
          >
        </div>
        <div class="mt-24">
          <RecaptchaV2 @load-callback="onVerify" />
        </div>
      </div>
      <!-- ========== Success ========== -->
      <div
        class="success-step text-center"
        v-show="currentStep === 3"
      >
        <PlaneIcon />

        <h2>Thank You for Sharing Your Story!</h2>
        <p class="text-m m-0">
          Your contribution helps us inspire and connect our community. If we need any additional information, our team will reach out to you.
        </p>
        <div>
          <a
            href="/alumni-corner/alumni-voices"
            class="btn outlined transparent wide"
            >View Other Stories</a
          >
          <a
            href="/programs"
            class="btn wide"
            >Explore Programs</a
          >
        </div>
      </div>
    </StepsLayout>
  </div>
</template>

<style lang="scss">
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables.scss';
@import 'bootstrap/scss/mixins.scss';

.ck.ck-editor__editable_inline {
  min-height: 230px;
}

.success-step {
  display: flex;
  flex-direction: column;
  gap: 40px;

  svg {
    margin: 0 auto;
  }

  > div {
    display: flex;
    gap: 33px;
    justify-content: center;
    align-items: center;

    @include media-breakpoint-down(md) {
      flex-direction: column;
      gap: 16px;
    }
  }
}
</style>
